import React, { ChangeEvent, FormEvent, MouseEvent, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { IGitTrailResponseQueryFilters } from '../../../utils/GitTrail/gitTrail.interface';
import { addDays, getDateToday, substractDays } from '../../../utils/Date/transformDate';
import IUserList from '../../../utils/UserList/UserList.interface';
import Select from '../../../library/Select/Select';
import Button from '../../../library/Button/button';

interface Props {
  users: IUserList[] | null;
  organizations: string[] | null;
  operations: string[] | null;
  onSubmit: (filterObject: IGitTrailResponseQueryFilters) => void;
}

const FilterBar = ({ users, organizations, operations, onSubmit }: Props) => {
  const timePresets = ['Last 2 days', 'Last 1 week', 'Last 30 Days', 'Custom'];

  const [showCustomDates, setShowCustomDates] = useState<boolean>(false);
  const [orgValue, setOrgValue] = useState<string>('');
  const [userValue, setUserValue] = useState<string>('');
  const [operationValue, setOperationValue] = useState<string>('');
  const [timePresetValue, setTimePresetValue] = useState<string>(timePresets[0]);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>();
  const userList = users
    ? users
        .filter((user) => (orgValue ? user.companyName === orgValue && user.email : user.email))
        .map((user) => user.email ?? '')
    : [];

  // Change event handlers
  const handleOrgChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setOrgValue(value);
  };

  const handleUserChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setUserValue(value);
  };

  const handleOperationChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setOperationValue(value);
  };

  const handleTimePresetValueChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === timePresets[3]) {
      setShowCustomDates(true);
    } else {
      setShowCustomDates(false);
    }
    setTimePresetValue(value);
  };

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
  };

  // Submit Handler
  const handleSubmitFilter = (e: FormEvent<Element>) => {
    e.preventDefault();
    const today = getDateToday();
    let start: number = Date.parse(today);
    let end: number = Date.now();

    switch (timePresetValue) {
      case timePresets[0]:
        start = Date.parse(substractDays(today, 2));
        break;
      case timePresets[1]:
        start = Date.parse(substractDays(today, 7));
        break;
      case timePresets[2]:
        start = Date.parse(substractDays(today, 30));
        break;
      case timePresets[3]:
        if (!startDate) return;
        if (endDate) {
          end = Date.parse(addDays(endDate, 1));
        }
        if (startDate) {
          start = Date.parse(startDate.toDateString());
        }
        break;
    }

    const filterObject: IGitTrailResponseQueryFilters = {
      start,
      end,
      org: orgValue,
      email: userValue,
      operation: operationValue
    };
    onSubmit(filterObject);
  };

  return (
    <div className="bg-offwhite rounded-md border border-gray p-2 mt-2">
      <div className="flex gap-2 flex-wrap">
        {organizations?.length ? (
          <Select
            id="filter-org-select"
            value={orgValue}
            onChange={handleOrgChange}
            options={organizations}
            error={null}
            label="Select Organization"
          />
        ) : (
          <></>
        )}
        {userList?.length ? (
          <Select
            id="filter-user-select"
            value={userValue}
            onChange={handleUserChange}
            options={userList}
            error={null}
            label="Select User"
          />
        ) : (
          <></>
        )}
        {operations ? (
          <Select
            id="filter-time-select"
            value={operationValue}
            onChange={handleOperationChange}
            options={operations}
            error={null}
            label="Select Operations"
          />
        ) : (
          <></>
        )}
        {timePresets ? (
          <Select
            id="filter-time-select"
            value={timePresetValue}
            onChange={handleTimePresetValueChange}
            options={timePresets}
            error={null}
            noLable
          />
        ) : (
          <></>
        )}
        {showCustomDates && (
          <div className="flex gap-2">
            <DatePicker
              id="start-date-select"
              className="sm:w-52 lg:w-60 bg-right bg-size-32 border border-light-gray px-4 py-2 rounded-md bg-no-repeat overflow-hidden"
              selected={startDate}
              onChange={handleStartDateChange}
              placeholderText="Select Start Date"
              dateFormat="yyyy-MM-dd"
              maxDate={endDate ? new Date(endDate) : new Date()}
              required
            />
            <DatePicker
              id="end-date-select"
              className="sm:w-52 lg:w-60 bg-right bg-size-32 border border-light-gray px-4 py-2 rounded-md bg-no-repeat overflow-hidden"
              selected={endDate}
              onChange={handleEndDateChange}
              placeholderText="Select End Date"
              dateFormat="yyyy-MM-dd"
              maxDate={new Date()}
              minDate={startDate ? new Date(startDate) : undefined}
            />
          </div>
        )}
        <div className="flex-grow flex justify-end">
          <Button
            id="submit-filters"
            className="h-10 px-4 bg-black text-white rounded-md focus:outline-none border border-spacing-1 border-black"
            onclick={handleSubmitFilter}
          >
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
